import { informationSecurityCybersecurityEnumsDataPotentiallyCompromisedOptionEnum } from '@/gen/types/InformationSecurityCybersecurityEnumsDataPotentiallyCompromisedOption';
import { informationSecurityCybersecurityEnumsIncidentAffectsACustomerOptionEnum } from '@/gen/types/InformationSecurityCybersecurityEnumsIncidentAffectsACustomerOption';
import { informationSecurityCybersecurityEnumsPotentiallyCompromisedDataOptionEnum } from '@/gen/types/InformationSecurityCybersecurityEnumsPotentiallyCompromisedDataOption';
import { informationSecurityCybersecurityEnumsUnauthorizedAccessDetectedOptionEnum } from '@/gen/types/InformationSecurityCybersecurityEnumsUnauthorizedAccessDetectedOption';
import { yesNoOptionEnum } from '@/gen/types/YesNoOption';

import { whenPathEquals, whenPathIncludes } from './conditions';
import type { IncidentFieldRule } from './types';

export const informationSecurityCybersecurityFieldRules: IncidentFieldRule[] = [
    {
        key: 'subCategory',
        label: 'What kind of cybersecurity incident is this?',
        description: 'Select the type, e.g. malware, phishing, data breach, DDoS.',
        order: 90,
    },
    {
        key: 'dataPotentiallyCompromised',
        label: 'Was any data compromised?',
        description: 'Select Yes if data may have been accessed, stolen, or altered.',
        order: 100,
    },
    {
        key: 'potentiallyCompromisedData',
        label: 'What type of data was involved?',
        description: 'Select the data type, e.g. personal data, credentials, business-critical.',
        order: 110,
        visibleWhen: whenPathEquals(
            'dataPotentiallyCompromised',
            informationSecurityCybersecurityEnumsDataPotentiallyCompromisedOptionEnum.Yes,
        ),
        requiredWhen: whenPathEquals(
            'dataPotentiallyCompromised',
            informationSecurityCybersecurityEnumsDataPotentiallyCompromisedOptionEnum.Yes,
        ),
    },
    {
        key: 'potentiallyCompromisedDataExplanation',
        label: 'Potentially compromised data explanation',
        kind: 'textarea',
        order: 120,
        visibleWhen: whenPathIncludes(
            'potentiallyCompromisedData',
            informationSecurityCybersecurityEnumsPotentiallyCompromisedDataOptionEnum.Other,
        ),
        requiredWhen: whenPathIncludes(
            'potentiallyCompromisedData',
            informationSecurityCybersecurityEnumsPotentiallyCompromisedDataOptionEnum.Other,
        ),
    },
    {
        key: 'incidentAffectsACustomer',
        label: 'Does this affect a customer?',
        description: 'Select Yes if any customer data, systems, or services were impacted.',
        order: 130,
    },
    {
        key: 'affectedCustomer',
        label: 'Affected customer',
        order: 140,
        visibleWhen: whenPathEquals(
            'incidentAffectsACustomer',
            informationSecurityCybersecurityEnumsIncidentAffectsACustomerOptionEnum.Yes,
        ),
        requiredWhen: whenPathEquals(
            'incidentAffectsACustomer',
            informationSecurityCybersecurityEnumsIncidentAffectsACustomerOptionEnum.Yes,
        ),
    },
    {
        key: 'immediateOperationalImpactNoticed',
        label: 'Was there any immediate impact on systems or services?',
        description: 'Select Yes if systems, networks, or services were impacted.',
        order: 150,
    },
    {
        key: 'immediateOperationalImpactNoticedExplanation',
        label: 'Please describe the impact',
        description: 'Briefly describe the operational or business impact observed.',
        kind: 'textarea',
        order: 160,
        visibleWhen: whenPathEquals('immediateOperationalImpactNoticed', yesNoOptionEnum.Yes),
        requiredWhen: whenPathEquals('immediateOperationalImpactNoticed', yesNoOptionEnum.Yes),
    },
    {
        key: 'primaryImpactedAssetType',
        label: 'Which type of asset is affected?',
        description: 'Select asset type, e.g. server, network device, endpoint, application.',
        order: 170,
    },
    {
        key: 'criticalityOfImpactedAsset',
        label: 'How critical is the affected system?',
        description: 'Rate the system\'s criticality based on its importance to operations.',
        order: 180,
    },
    {
        key: 'detectionSource',
        label: 'How was this detected?',
        description: 'Select how it was discovered, e.g. SOC, monitoring, user report.',
        order: 190,
    },
    {
        key: 'unauthorizedAccessDetected',
        label: 'Was any unauthorized access detected?',
        description: 'Select Yes if unauthorized access to systems or data was identified.',
        order: 200,
    },
    {
        key: 'unauthorizedAccessDetectedExplanation',
        label: 'Unauthorized access explanation',
        kind: 'textarea',
        order: 210,
        visibleWhen: whenPathEquals(
            'unauthorizedAccessDetected',
            informationSecurityCybersecurityEnumsUnauthorizedAccessDetectedOptionEnum.Yes,
        ),
        requiredWhen: whenPathEquals(
            'unauthorizedAccessDetected',
            informationSecurityCybersecurityEnumsUnauthorizedAccessDetectedOptionEnum.Yes,
        ),
    },
    {
        key: 'systemAlreadyBeenIsolatedOrAccessRestricted',
        label: 'Has the system already been isolated?',
        description: 'Select Yes if the affected system has been disconnected or restricted.',
        order: 220,
    },
    {
        key: 'incidentAdditionalInformation',
        label: 'Additional information',
        kind: 'textarea',
        order: 230,
    },
];
